import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { calculateShiftTimes } from "../../../contexts/utils/shift-times";
import { useSelfSchedulingPublish } from "../../../hooks/useSelfSchedulingPublish";
import { adjustNightShiftDate, getShiftTypeFromStartTime, getStaffingDate } from "../../../utils/dateUtils";
import { type MetricsData, Preferences, type SelfScheduleCalendarDaysData, SelfSchedulingPreference, type SpecificPreference } from "../../../utils/models";
import { calculateNextPeriod, calculatePreviousPeriod, calculateSchedulingPeriod, SHIFTS_PER_WEEK_BY_EMPLOYMENT_TYPE } from "../util/utils";

export const useSelfSchedulingState = () => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar, activeBedsideNurse, refreshNursesData } = useHospitalData();
	const { mutateAsync: publishSelfScheduling, isPending: isPublishing } = useSelfSchedulingPublish();

	const metricsRef = useRef<HTMLDivElement>(null);
	const [daysData, setDaysData] = useState<SelfScheduleCalendarDaysData>({});
	const [isPublishWarningOpen, setIsPublishWarningOpen] = useState<boolean>(false);
	const [selectedDate, setSelectedDate] = useState<DateTime>();

	const [viewedPeriod, setViewedPeriod] = useState<{ periodStart: DateTime; periodEnd: DateTime }>();

	const currentMetrics = useMemo<MetricsData>(() => {
		const signupShiftsCurrentAmount = Object.values(daysData).filter((dayData) => dayData.dayPreference === SelfSchedulingPreference.WANT_TO_WORK).length;
		return { signupShiftsCurrentAmount };
	}, [daysData]);

	const shiftsPerWeekByEmploymentType = activeBedsideNurse ? (SHIFTS_PER_WEEK_BY_EMPLOYMENT_TYPE[activeBedsideNurse.employmentType] ?? 0) : 0;

	const schedulingPeriod = useMemo(() => {
		if (viewedPeriod) return viewedPeriod;

		if (!chosenDepartment?.selfScheduling) return;

		return calculateSchedulingPeriod(chosenDepartment.selfScheduling, chosenDepartment.timezone);
	}, [chosenDepartment, viewedPeriod]);

	const totalWeeksInSchedule = useMemo<number>(() => {
		if (!schedulingPeriod) return 0;

		const { periodStart, periodEnd } = schedulingPeriod;
		const diffInDays = Math.floor(periodEnd.diff(periodStart, "days").days);
		const weekCount = Math.ceil((diffInDays + 1) / 7);

		return weekCount;
	}, [schedulingPeriod]);

	const signUpShiftsTargetAmount = shiftsPerWeekByEmploymentType * totalWeeksInSchedule;

	useEffect(() => {
		if (!activeBedsideNurse || !chosenDepartment?.selfScheduling) return;

		const initialDaysData: SelfScheduleCalendarDaysData = {};

		const ptoPreferences = activeBedsideNurse.employeeSpecificPreferences.filter((pref) => pref.type === Preferences.Avoid);
		const workPreferences = activeBedsideNurse.employeeSpecificPreferences.filter((pref) => pref.type === Preferences.Prefer);

		ptoPreferences.forEach((pref) => {
			const rangeStart = getStaffingDate(pref.start_date, chosenDepartment.dayStart).startOf("day");
			const rangeEnd = getStaffingDate(pref.end_date.minus({ minutes: 1 }), chosenDepartment.dayStart).startOf("day");
			let current = rangeStart;
			while (current <= rangeEnd) {
				const dateKey = current.toFormat("yyyy-MM-dd");
				initialDaysData[dateKey] = { date: current, dayPreference: SelfSchedulingPreference.PTO };
				current = current.plus({ days: 1 });
			}
		});

		workPreferences.forEach((pref) => {
			// Written with the scheduled grace, so read back with it. PTO above stays on the raw dayStart.
			const prefDate = getStaffingDate(pref.start_date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day");
			const dateKey = prefDate.toFormat("yyyy-MM-dd");

			if (!initialDaysData[dateKey] || initialDaysData[dateKey].dayPreference !== SelfSchedulingPreference.PTO) {
				const shiftType = getShiftTypeFromStartTime(pref.start_date);

				initialDaysData[dateKey] = {
					date: prefDate,
					dayPreference: SelfSchedulingPreference.WANT_TO_WORK,
					shiftType,
					startTime: pref.start_date,
					endTime: pref.end_date,
					originalPreferenceId: pref.id,
				};
			}
		});

		if (Object.keys(initialDaysData).length) setDaysData(initialDaysData);
	}, [activeBedsideNurse, chosenDepartment]);

	const handleOnDayClick = useCallback(
		(date: DateTime) => {
			const dateKey = date.toFormat("yyyy-MM-dd");
			const existingDay = daysData[dateKey];

			if (existingDay?.dayPreference === SelfSchedulingPreference.PTO) return;

			if (!existingDay || existingDay.dayPreference !== SelfSchedulingPreference.WANT_TO_WORK) {
				const shifts = Object.values(daysData).filter((d) => d.dayPreference === SelfSchedulingPreference.WANT_TO_WORK);

				let startTime: DateTime;
				let endTime: DateTime;

				if (shifts.length) {
					const shiftPatternCounts = new Map<string, { count: number; startTime: DateTime; endTime: DateTime }>();

					shifts.forEach((shift) => {
						if (!shift.startTime || !shift.endTime) return;
						const key = `${shift.startTime.hour}:${shift.startTime.minute}-${shift.endTime.hour}:${shift.endTime.minute}`;
						const existing = shiftPatternCounts.get(key);
						if (existing) existing.count++;
						else shiftPatternCounts.set(key, { count: 1, startTime: shift.startTime, endTime: shift.endTime });
					});

					let mostCommonPattern: { count: number; startTime: DateTime; endTime: DateTime } | undefined;
					shiftPatternCounts.forEach((pattern) => {
						if (!mostCommonPattern || pattern.count > mostCommonPattern.count) {
							mostCommonPattern = pattern;
						}
					});

					if (mostCommonPattern) {
						startTime = adjustNightShiftDate(
							date.set({ hour: mostCommonPattern.startTime.hour, minute: mostCommonPattern.startTime.minute }),
							date,
							chosenDepartment.dayStart,
							chosenDepartment.scheduled_shift_delta_minutes,
						);
						endTime = adjustNightShiftDate(
							date.set({ hour: mostCommonPattern.endTime.hour, minute: mostCommonPattern.endTime.minute }),
							date,
							chosenDepartment.dayStart,
							chosenDepartment.scheduled_shift_delta_minutes,
						);
					} else {
						const shiftTimes = calculateShiftTimes({
							day: { date },
							shiftNo: 0,
							nurseRole: activeBedsideNurse?.role,
							chosenDepartment,
						});
						startTime = shiftTimes.startTime;
						endTime = shiftTimes.endTime;
					}
				} else {
					const shiftTimes = calculateShiftTimes({ day: { date }, shiftNo: 0, nurseRole: activeBedsideNurse?.role, chosenDepartment });
					startTime = shiftTimes.startTime;
					endTime = shiftTimes.endTime;
				}

				if (endTime <= startTime) endTime = endTime.plus({ days: 1 });

				setDaysData((prevState) => ({
					...prevState,
					[dateKey]: {
						date,
						dayPreference: SelfSchedulingPreference.WANT_TO_WORK,
						shiftType: getShiftTypeFromStartTime(startTime),
						startTime,
						endTime,
					},
				}));
				setSelectedDate(date);
				return;
			}

			if (selectedDate?.toFormat("yyyy-MM-dd") !== dateKey) return setSelectedDate(date);

			setDaysData(({ [dateKey]: _, ...rest }) => rest);
			setSelectedDate(undefined);
		},
		[daysData, selectedDate, activeBedsideNurse, chosenDepartment],
	);

	const handleTimeChange = useCallback(
		(newStartTime: DateTime, newEndTime: DateTime) => {
			if (!selectedDate) return;

			setDaysData((prevState) => {
				const dateKey = selectedDate.toFormat("yyyy-MM-dd");

				const existing = prevState[dateKey];
				if (!existing) return prevState;

				const shiftType = getShiftTypeFromStartTime(newStartTime);

				return { ...prevState, [dateKey]: { ...existing, startTime: newStartTime, endTime: newEndTime, shiftType } };
			});
		},
		[selectedDate],
	);

	const togglePublishWarning = useCallback(() => setIsPublishWarningOpen((prev) => !prev), []);

	const handlePerformPublish = useCallback(async () => {
		if (!activeBedsideNurse) return showErrorSnackbar(t("error.nurse.not.found"), {});

		const preferences: Array<{ startDate: string; endDate: string }> = [];
		for (const day of Object.values(daysData)) {
			if (day.dayPreference !== SelfSchedulingPreference.WANT_TO_WORK || !day.startTime || !day.endTime) continue;
			preferences.push({ startDate: day.startTime.toISO() as string, endDate: day.endTime.toISO() as string });
		}

		await publishSelfScheduling({ preferences });
		await refreshNursesData(chosenDepartment.department_id);

		togglePublishWarning();
	}, [daysData, activeBedsideNurse, chosenDepartment, refreshNursesData, togglePublishWarning, t, publishSelfScheduling, showErrorSnackbar]);

	const hasChanges = useMemo<boolean>(() => {
		if (!activeBedsideNurse) return false;

		const existingPreferences = new Map(
			activeBedsideNurse.employeeSpecificPreferences
				.filter((pref) => pref.type === Preferences.Prefer)
				// Same key the calendar uses, or a graced preference reads as an add plus a remove.
				.map((pref) => [
					getStaffingDate(pref.start_date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat("yyyy-MM-dd"),
					pref,
				])
				.filter(([dateKey]) => dateKey !== null) as Array<[string, SpecificPreference]>,
		);

		const hasAddedOrUpdated = Object.entries(daysData).some(([dateKey, day]) => {
			if (day.dayPreference !== SelfSchedulingPreference.WANT_TO_WORK) return false;

			const existing = existingPreferences.get(dateKey);
			if (!existing) return true;

			const hasChanged = !day.startTime?.hasSame(existing.start_date, "minute") || !day.endTime?.hasSame(existing.end_date, "minute");
			if (hasChanged) return true;
			return !day.startTime?.hasSame(existing.start_date, "minute") || !day.endTime?.hasSame(existing.end_date, "minute");
		});

		if (hasAddedOrUpdated) return true;

		const hasRemoved = Array.from(existingPreferences.keys()).some((dateKey) => {
			const dayData = daysData[dateKey];
			return !dayData || dayData.dayPreference !== SelfSchedulingPreference.WANT_TO_WORK;
		});

		return hasRemoved;
	}, [daysData, activeBedsideNurse, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	const navigateToPreviousPeriod = useCallback(() => {
		if (!schedulingPeriod || !chosenDepartment?.selfScheduling) return;

		const currentSelfScheduling = {
			...chosenDepartment.selfScheduling,
			periodStart: schedulingPeriod.periodStart,
			periodEnd: schedulingPeriod.periodEnd,
		};

		const previousPeriod = calculatePreviousPeriod(currentSelfScheduling);

		setViewedPeriod(previousPeriod);
	}, [schedulingPeriod, chosenDepartment]);

	const navigateToNextPeriod = useCallback(() => {
		if (!schedulingPeriod || !chosenDepartment?.selfScheduling) return;

		const currentSelfScheduling = {
			...chosenDepartment.selfScheduling,
			periodStart: schedulingPeriod.periodStart,
			periodEnd: schedulingPeriod.periodEnd,
		};

		const nextPeriod = calculateNextPeriod(currentSelfScheduling);

		setViewedPeriod(nextPeriod);
	}, [schedulingPeriod, chosenDepartment]);

	const navigateToCurrentPeriod = useCallback(() => {
		setViewedPeriod(undefined);
	}, []);

	const copyLastMonthPreferences = useCallback(() => {
		if (!activeBedsideNurse || !schedulingPeriod || !chosenDepartment?.selfScheduling) return;

		const currentSelfScheduling = { ...chosenDepartment.selfScheduling, periodStart: schedulingPeriod.periodStart, periodEnd: schedulingPeriod.periodEnd };

		const previousPeriod = calculatePreviousPeriod(currentSelfScheduling);

		const lastPeriodPreferences = Object.values(daysData).filter(
			(dayData) =>
				dayData.dayPreference === SelfSchedulingPreference.WANT_TO_WORK &&
				dayData.date >= previousPeriod.periodStart &&
				dayData.date <= previousPeriod.periodEnd,
		);

		if (!lastPeriodPreferences.length) return showErrorSnackbar(t("no.preferences.from.last.month"), {});

		const newDaysData: SelfScheduleCalendarDaysData = { ...daysData };

		lastPeriodPreferences.forEach((dayData) => {
			const daysDiff = Math.floor(dayData.date.diff(previousPeriod.periodStart, "days").days);
			const newDate = schedulingPeriod.periodStart.plus({ days: daysDiff });

			if (newDate >= schedulingPeriod.periodStart && newDate <= schedulingPeriod.periodEnd) {
				const dateKey = newDate.toFormat("yyyy-MM-dd");

				if (newDaysData[dateKey]?.dayPreference === SelfSchedulingPreference.PTO) return;

				const startTime = dayData.startTime
					? adjustNightShiftDate(
							newDate.set({ hour: dayData.startTime.hour, minute: dayData.startTime.minute }),
							newDate,
							chosenDepartment.dayStart,
							chosenDepartment.scheduled_shift_delta_minutes,
						)
					: newDate;
				const endTime = dayData.endTime
					? adjustNightShiftDate(
							newDate.set({ hour: dayData.endTime.hour, minute: dayData.endTime.minute }),
							newDate,
							chosenDepartment.dayStart,
							chosenDepartment.scheduled_shift_delta_minutes,
						)
					: newDate.plus({ hours: 8 });

				newDaysData[dateKey] = {
					date: newDate,
					dayPreference: SelfSchedulingPreference.WANT_TO_WORK,
					shiftType: dayData.shiftType ?? getShiftTypeFromStartTime(startTime),
					startTime,
					endTime,
				};
			}
		});

		setDaysData(newDaysData);
	}, [activeBedsideNurse, schedulingPeriod, daysData, showErrorSnackbar, t, chosenDepartment]);

	const isViewingCurrentPeriod = viewedPeriod === undefined;
	const isViewingPastPeriod = useMemo(() => {
		if (!schedulingPeriod || !chosenDepartment?.selfScheduling) return false;

		const activePeriodStart = chosenDepartment.selfScheduling.periodStart.startOf("day");
		const viewedPeriodEnd = schedulingPeriod.periodEnd.startOf("day");

		return viewedPeriodEnd < activePeriodStart;
	}, [schedulingPeriod, chosenDepartment]);

	const isViewingLockedPeriod = useMemo(() => {
		if (!schedulingPeriod || !chosenDepartment?.selfScheduling) return false;

		const isLocked = chosenDepartment.selfScheduling.locked;
		if (!isLocked) return false;

		const departmentPeriodStart = chosenDepartment.selfScheduling.periodStart;
		const departmentPeriodEnd = chosenDepartment.selfScheduling.periodEnd;

		return schedulingPeriod.periodStart.hasSame(departmentPeriodStart, "day") && schedulingPeriod.periodEnd.hasSame(departmentPeriodEnd, "day");
	}, [schedulingPeriod, chosenDepartment]);

	return {
		daysData,
		setDaysData,
		currentMetrics,
		metricsRef,
		isPublishWarningOpen,
		isPublishing,
		handleOnDayClick,
		handleTimeChange,
		togglePublishWarning,
		handlePerformPublish,
		signUpShiftsTargetAmount,
		shiftsPerWeekByEmploymentType,
		totalWeeksInSchedule,
		selectedDate,
		setSelectedDate,
		hasChanges,
		navigateToPreviousPeriod,
		navigateToNextPeriod,
		navigateToCurrentPeriod,
		isViewingCurrentPeriod,
		schedulingPeriod,
		isViewingPastPeriod,
		isViewingLockedPeriod,
		copyLastMonthPreferences,
	};
};
